import React, { useState, useCallback } from "react"
import Modal from "react-modal"
import PropTypes from "prop-types"

const Field = ({ handle, heading, errors = {}, required = false }) => (
    <div className={`field${required ? " is-required" : ""}`}>
        <div className={`input${handle in errors ? " is-invalid" : ""}`}>
            <input id={`fields-${handle}`} type="text" name={`fields[${handle}]`} placeholder={heading} />
        </div>
        {handle in errors && (
            <ul className="form__errors"><li>{errors[handle]}</li></ul>
        )}
    </div>
)

Field.propTypes = {
    handle: PropTypes.string.isRequired,
    heading: PropTypes.string.isRequired,
    errors: PropTypes.object,
    required: PropTypes.bool,
}

const EnquiryForm = ({ onSubmit, errors, isLoading }) => (
    <div>
        <p className="modal__heading">Your enquiry</p>

        <form
            className="modal__form"
            method="post"
            acceptCharset="utf-8"
            encType="multipart/form-data"
            onSubmit={onSubmit}
        >
            <input type="hidden" name={window.csrfTokenName} value={window.csrfTokenValue} />
            <input type="hidden" name="handle" value="enquiryForm" />
            <input type="hidden" name="fields[productCode]" value={window.productName} />
            <input type="hidden" name="fields[productUrl]" value={window.location} />

            <Field handle="enquirerName" heading="Name" errors={errors} required />
            <Field handle="email" heading="Email" errors={errors} required />
            <Field handle="phone" heading="Phone" errors={errors} required />
            <Field handle="companyName" heading="Company name" errors={errors} />

            <div className="radio-group">
                <div className="radio-group__heading">Preferred method of contact</div>
                <input type="radio" id="fields-contactMethod-phone" name="fields[contactMethod]" value="phone" />
                <label htmlFor="fields-contactMethod-phone">Phone</label>
                <input type="radio" id="fields-contactMethod-email" name="fields[contactMethod]" value="email" />
                <label htmlFor="fields-contactMethod-email">Email</label>
            </div>

            <div className="field">
                <label htmlFor="fields-contactMethod-message" className="label">Your message</label>
                <textarea name="fields[message]" rows="4" />
            </div>

            <div className={isLoading ? "submit submit--loading" : "submit"}>
                <input className="btn" type="submit" value="Submit" disabled={isLoading} />
            </div>
        </form>
    </div>
)

EnquiryForm.propTypes = {
    onSubmit: PropTypes.func.isRequired,
    errors: PropTypes.object.isRequired,
    isLoading: PropTypes.bool.isRequired,
}

const Confirmation = () => (
    <div>
        <p className="modal__heading">Your enquiry</p>
        <div className="modal__form">
            <h4>Thanks for your enquiry</h4>
            <p>We will get back to you shortly</p>
        </div>
    </div>
)

const EnquiryModal = ({ isOpen, toggleModal, hit }) => {
    const [isLoading, setLoading] = useState(false)
    const [errors, setErrors] = useState({})
    const [networkError, setNetworkError] = useState("")
    const [submitted, setSubmitted] = useState(false)

    const onSubmit = useCallback(async (event) => {
        event.preventDefault()
        setLoading(true)
        setNetworkError("")

        try {
            const response = await fetch("/actions/formie/submissions/submit", {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                },
                credentials: "same-origin",
                body: new FormData(event.target),
            })

            const json = await response.json()

            if ("errors" in json) {
                setErrors(json.errors)
            } else {
                setSubmitted(true)
                window.dataLayer?.push?.({ event: "enquiry-modal-submit" })
            }
        } catch (err) {
            setNetworkError("An unexpected error occurred, please try again later.")
        } finally {
            setLoading(false)
        }
    }, [])

    const onClose = useCallback(() => {
        setErrors({})
        setSubmitted(false)
        toggleModal()
    }, [toggleModal])

    return (
        <Modal
            isOpen={isOpen}
            shouldCloseOnOverlayClick
            contentLabel="EnquiryForm"
            onRequestClose={onClose}
            closeTimeoutMS={600}
            className={{
                base: "modal",
                afterOpen: "modal--after-open",
                beforeClose: "modal--before-close",
            }}
            overlayClassName={{
                base: "modal-overlay",
                afterOpen: "modal-overlay--after-open",
                beforeClose: "modal-overlay--before-close",
            }}
        >
            <button className="modal__close" onClick={toggleModal}>
                Close <svg><use xlinkHref="#cross-icon" /></svg>
            </button>

            {networkError && <div className="error">{networkError}</div>}
            {submitted
                ? <Confirmation />
                : <EnquiryForm
                    hit={hit}
                    onSubmit={onSubmit}
                    errors={errors}
                    isLoading={isLoading}
                />}
        </Modal>
    )
}

EnquiryModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    toggleModal: PropTypes.func.isRequired,
    hit: PropTypes.object,
}
export default EnquiryModal